// store/admin/sme-management/store/_saga.js

import { call, put, takeEvery, delay } from 'redux-saga/effects';
import * as actions from './_actions';
import * as types from './_actionTypes';
import SMEHelper from '../../../utils/API/BackendHelper/admin/sme'; // adjust path if needed
import API_ENDPOINTS from '../../../utils/API/endpoints';
import ApiHelper from '../../../utils/API/ApiHelper';


function cleanPayload(obj = {}) {
  const copy = { ...obj };
  Object.keys(copy).forEach((k) => {
    const v = copy[k];
    if (v === undefined || v === null || (typeof v === 'string' && v.trim() === '')) {
      delete copy[k];
    }
  });
  return copy;
}

/**
 * CREATE SME
 * payload expected:
 * { email, password, firstName, lastName, subject, phone, qualification, experience }
 */
// function* createSMESaga(action) {
//   try {
//     const formData = action.payload || {};

//     const backendData = {
//       email: formData.email || formData.contact_email,
//       password: formData.password || formData.admin_password,
//       firstName: formData.firstName || formData.admin_first_name,
//       lastName: formData.lastName || formData.admin_last_name,
//       subject: formData.subject,
//       phone: formData.phone || formData.contact_phone,
//       qualification: formData.qualification,
//       experience: formData.experience
//     };

//     const payload = cleanPayload(backendData);

//     console.log('Creating SME - payload:', payload);

//     const response = yield call(SMEHelper.createSME, payload);

//     console.log('Create SME response:', response);

//     if (response && response.success) {
//       // backend returns response.data.user (based on your sample)
//       const user = response.data?.user || response.data;

//       const newSme = {
//         id: user.id,
//         username: user.username || user.email,
//         first_name: user.first_name,
//         last_name: user.last_name,
//         phone: user.phone,
//         role: user.role,
//         is_active: user.is_active,
//         college_id: user.college_id,
//         created_at: user.created_at,
//         updated_at: user.updated_at,
//         profile: response.data?.profile || null
//       };

//       yield put(actions.createSMESuccess(newSme));
//       // small UX delay (optional)
//       yield delay(1500);
//       // navigation can be handled by component or via this action
//       yield put({ type: 'NAVIGATE_TO_SME_MANAGEMENT' });
//     } else {
//       yield put(actions.createSMEFailure(response?.message || 'Failed to create SME'));
//     }
//   } catch (error) {
//     console.error('Unexpected error in createSMESaga:', error);
//     yield put(actions.createSMEFailure('An unexpected error occurred'));
//   }
// }


// Replace the existing createSMESaga with this implementation

function normalizeSubjectsFromForm(formData) {
  // Accept multiple possible shapes:
  // - formData.subjects: an array of strings (preferred)
  // - formData.subject: a single string
  // - formData.subject_list or any variant can be handled similarly if needed
  const subjectsArr = [];

  if (Array.isArray(formData.subjects)) {
    formData.subjects.forEach((s) => {
      if (typeof s === 'string' && s.trim()) subjectsArr.push(s.trim());
    });
  }

  if ((formData.subjects === undefined || subjectsArr.length === 0) && formData.subject) {
    // fallback: single subject string
    if (typeof formData.subject === 'string' && formData.subject.trim()) {
      subjectsArr.push(formData.subject.trim());
    }
  }

  // if UI uses `currentSubject` or `subjectText` (typed but not added) include it:
  if (formData.currentSubject && typeof formData.currentSubject === 'string' && formData.currentSubject.trim()) {
    const trimmed = formData.currentSubject.trim();
    if (!subjectsArr.includes(trimmed)) subjectsArr.push(trimmed);
  }

  return subjectsArr;
}

function* createSMESaga(action) {
  try {
    const formData = action.payload || {};

    // Build subjects safely (prefer array but accept single)
    const subjects = normalizeSubjectsFromForm(formData);

    // Build backend payload using expected backend keys.
    // NOTE: Confirm backend expects 'subjects' (array) and 'experienceYears' (number).
    // If backend expects different names (e.g. 'experience'), change accordingly.
    const backendData = {
      email: formData.email || formData.contact_email,
      password: formData.password || formData.admin_password,
      firstName: formData.firstName || formData.admin_first_name,
      lastName: formData.lastName || formData.admin_last_name,

      // Use 'subjects' array (not 'subject' singular)
      ...(subjects.length > 0 ? { subjects } : {}),

      // phone fallback
      phone: formData.phone || formData.contact_phone,

      // qualification
      qualification: formData.qualification,

      // Normalize experience -> prefer numeric field name expected by backend.
      // If backend expects 'experienceYears', use that. If it expects 'experience', use that instead.
      ...(formData.experienceYears !== undefined && formData.experienceYears !== null
        ? { experienceYears: Number(formData.experienceYears) }
        : formData.experience !== undefined && formData.experience !== null
          ? { experienceYears: Number(formData.experience) } // convert old 'experience' shape
          : {}),

      // include any other optional fields user may pass
      specialization: formData.specialization,
      bio: formData.bio
    };

    // Clean payload (remove empty/null/undefined)
    const payload = cleanPayload(backendData);

    // Debug: important — verify exact payload before network call
    console.log('Creating SME - payload (from saga):', JSON.stringify(payload, null, 2));

    // Call backend
    const response = yield call(SMEHelper.createSME, payload);

    console.log('Create SME response:', response);

    if (response && response.success) {
      const user = response.data?.user || response.data;

      const newSme = {
        id: user.id,
        username: user.username || user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        phone: user.phone,
        role: user.role,
        is_active: user.is_active,
        college_id: user.college_id,
        created_at: user.created_at,
        updated_at: user.updated_at,
        profile: response.data?.profile || null
      };

      yield put(actions.createSMESuccess(newSme));
      yield delay(1500);
      yield put({ type: 'NAVIGATE_TO_SME_MANAGEMENT' });
    } else {
      yield put(actions.createSMEFailure(response?.message || 'Failed to create SME'));
    }
  } catch (error) {
    console.error('Unexpected error in createSMESaga:', error);
    // If the error has fields from backend, try to include helpful message
    const errMsg = (error && (error.message || error?.response?.data?.message)) || 'An unexpected error occurred';
    yield put(actions.createSMEFailure(errMsg));
  }
}

/**
 * FETCH SMEs (paginated)
 * action.payload can include params like { limit, offset, search }
 */
// function* fetchSMEsSaga(action) {
//   try {
//     const params = action.payload || {};

//     // Use your ApiHelper.getPaginated pattern like in college saga
//     const response = yield call(ApiHelper.getPaginated, API_ENDPOINTS.ADMIN.SMES, params);

//     if (response && response.success) {
//       // response.data expected as an array; adapt if your API returns { data, pagination }
//       const list = Array.isArray(response.data) ? response.data : (response.data?.smes || response.data?.users || []);

//       const smes = list.map(item => ({
//         id: item.id,
//         username: item.username || item.email,
//         first_name: item.first_name,
//         last_name: item.last_name,
//         phone: item.phone,
//         role: item.role,
//         is_active: item.is_active,
//         created_at: item.created_at,
//         updated_at: item.updated_at,
//         profile: item.profile || null
//       }));

//       yield put(actions.fetchSMEsSuccess(smes));
//     } else {
//       yield put(actions.fetchSMEsFailure(response?.message || 'Failed to fetch SMEs'));
//     }
//   } catch (error) {
//     console.error('Unexpected error in fetchSMEsSaga:', error);
//     yield put(actions.fetchSMEsFailure('An unexpected error occurred'));
//   }
// }
function* fetchSMEsSaga(action) {
  try {
    const params = action.payload || {};
    const response = yield call(ApiHelper.getPaginated, API_ENDPOINTS.ADMIN.SMES, params);

    if (response && response.success) {
      const list = Array.isArray(response.data) ? response.data : [];

      // Flatten the user data into the SME object
      const smes = list.map(item => ({
        id: item.id,
        user_id: item.user_id,
        subjects: item.subjects || [],
        specialization: item.specialization,
        qualification: item.qualification,
        experience_years: item.experience_years,
        bio: item.bio,
        languages: item.languages || [],
        is_available: item.is_available,
        is_active: item.is_active,
        created_at: item.created_at,
        updated_at: item.updated_at,
        
        // Flatten user data
        first_name: item.users?.first_name || '',
        last_name: item.users?.last_name || '',
        email: item.users?.username || '', // username is email
        phone: item.users?.phone || '',
        username: item.users?.username || '',
        
        // Additional stats
        coursesCount: item.coursesCount || 0,
        studentsCount: item.studentsCount || 0,
        averageRating: item.averageRating || '0'
      }));

      yield put(actions.fetchSMEsSuccess(smes));
    } else {
      yield put(actions.fetchSMEsFailure(response?.message || 'Failed to fetch SMEs'));
    }
  } catch (error) {
    console.error('Unexpected error in fetchSMEsSaga:', error);
    yield put(actions.fetchSMEsFailure('An unexpected error occurred'));
  }
}
/**
 * DELETE SME
 */
function* deleteSMESaga(action) {
  try {
    const smeId = action.payload;

    // Prefer using helper delete method; fallback to ApiHelper if needed
    let response;
    if (SMEHelper.deleteSME) {
      response = yield call(SMEHelper.deleteSME, smeId);
    } else {
      response = yield call(ApiHelper.delete, `${API_ENDPOINTS.ADMIN.SMES}/${smeId}`);
    }

    if (response && response.success) {
      yield put(actions.deleteSMESuccess(smeId));
    } else {
      yield put(actions.deleteSMEFailure(response?.message || 'Failed to delete SME'));
    }
  } catch (error) {
    console.error('Unexpected error in deleteSMESaga:', error);
    yield put(actions.deleteSMEFailure('An unexpected error occurred'));
  }
}

/**
 * UPDATE SME
 */
function* updateSMESaga(action) {
  try {
    const { smeId, updateData } = action.payload;

    const response = yield call(SMEHelper.updateSME, smeId, updateData);

    if (response && response.success) {
      // Transform the response to match the format used in the store
      const updatedSME = {
        id: response.data.id,
        user_id: response.data.user_id,
        subjects: response.data.subjects || [],
        specialization: response.data.specialization,
        qualification: response.data.qualification,
        experience_years: response.data.experience_years,
        bio: response.data.bio,
        languages: response.data.languages || [],
        is_available: response.data.is_available,
        is_active: response.data.is_active,
        created_at: response.data.created_at,
        updated_at: response.data.updated_at,
        
        // Flatten user data if present
        first_name: response.data.users?.first_name || updateData.firstName || '',
        last_name: response.data.users?.last_name || updateData.lastName || '',
        email: response.data.users?.username || updateData.email || '',
        phone: response.data.users?.phone || updateData.phone || '',
        username: response.data.users?.username || updateData.email || '',
        
        // Preserve stats
        coursesCount: response.data.coursesCount || 0,
        studentsCount: response.data.studentsCount || 0,
        averageRating: response.data.averageRating || '0'
      };

      yield put(actions.updateSMESuccess(updatedSME));
      
      // Refresh the SME list
      yield put(actions.fetchSMEsRequest());
    } else {
      yield put(actions.updateSMEFailure(response?.message || 'Failed to update SME'));
    }
  } catch (error) {
    console.error('Unexpected error in updateSMESaga:', error);
    yield put(actions.updateSMEFailure('An unexpected error occurred'));
  }
}

/**
 * Root saga for SME management
 */
function* adminSmeSaga() {
  yield takeEvery(types.FETCH_SMES_REQUEST, fetchSMEsSaga);
  yield takeEvery(types.CREATE_SME_REQUEST, createSMESaga);
  yield takeEvery(types.DELETE_SME_REQUEST, deleteSMESaga);
  yield takeEvery(types.UPDATE_SME_REQUEST, updateSMESaga);
}

export default adminSmeSaga;